// useTransientSurfaceState — the shell's transient view-surface state cells:
// the command palette's create-dialog seed, the History & Inspection overlay's
// loaded data, the content-finder's corpus, and the inline lifecycle-confirm
// transients (rename / delete). All of these are
// ephemeral surface state, not loaded domain data — they are parked at the
// foundation and read only by the downstream sub-hooks the orchestrator wires,
// so grouping them here keeps useShellFoundation under the size gate without
// changing any behaviour. Called ONCE, unconditionally, at the same position
// its cells occupied in the foundation body, so the hook-call order is
// unchanged.
export { useTransientSurfaceState };

import { useState } from "react";
import type { FinderDoc } from "../../components/Finder/finderIndex.js";
import type {
    ConversationRailItem,
    ResourceRef,
} from "../../components/Navigation/types.js";
import type { FileHistoryView, HistoryView } from "../ports.js";
import type { Renaming } from "./useLifecycleSurfaces.js";

const useTransientSurfaceState = () => {
    // The create dialog's seed title (P17): the palette's Enter-on-zero
    // pre-fills the material title with the typed query.
    const [createSeedTitle, setCreateSeedTitle] = useState("");
    // The History & Inspection overlay's data (loaded when it opens; null while
    // loading or when the read is unavailable — the http gap). `fileHistory`
    // holds the drilled resource's version history with the key it belongs to;
    // `historyVersionId` is the selected version (its diff comes from the view).
    const [historyView, setHistoryView] = useState<HistoryView | null>(null);
    const [fileHistory, setFileHistory] = useState<{
        readonly resourceKey: string;
        readonly view: FileHistoryView;
    } | null>(null);
    const [historyVersionId, setHistoryVersionId] = useState<string | null>(
        null,
    );
    // The content-finder's corpus (loaded when its overlay opens; empty until).
    // `finderLoading` shows a calm "Searching…" line instead of a false "No
    // matches" while the index read is in flight.
    const [finderDocs, setFinderDocs] = useState<readonly FinderDoc[]>([]);
    const [finderLoading, setFinderLoading] = useState(false);
    // The lifecycle pattern's transient state (master audit R5): which row is
    // being renamed inline, and which resource awaits delete confirmation.
    const [renaming, setRenaming] = useState<Renaming | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<ResourceRef | null>(
        null,
    );
    // The conversation awaiting delete confirmation (its own state — a
    // conversation is not a ResourceRef). Delete is the rare, heavier verb on
    // a conversation, so it gets the same confirm-then-undo forgiveness as a
    // resource delete.
    const [confirmDeleteConversation, setConfirmDeleteConversation] =
        useState<ConversationRailItem | null>(null);

    return {
        createSeedTitle,
        setCreateSeedTitle,
        historyView,
        setHistoryView,
        fileHistory,
        setFileHistory,
        historyVersionId,
        setHistoryVersionId,
        finderDocs,
        setFinderDocs,
        finderLoading,
        setFinderLoading,
        renaming,
        setRenaming,
        confirmDelete,
        setConfirmDelete,
        confirmDeleteConversation,
        setConfirmDeleteConversation,
    };
};
